import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import { EndorsementExposureUpdateRequest } from '../../../types/endorsements';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import { FastForm } from '../../fastForm';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import {
	getExposureIndexFromContext,
	getExposureObject,
	getPerilName,
} from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddEndorsementPerilModal';

interface Props {
	authData: AuthData;
	userHasWriteAccess?: boolean;
	endorsementData: EndorsementData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: 'auto',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * Added peril on an added or existing exposure on an endorsement. The url for this peril will be in the
 * format 'added-{index}', i.e 'added-0'.
 */
const AddedPerilDetails = (props: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams();
	const classes = useStyles();

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const { userHasWriteAccess, endorsementData, authData } = props;

	const {
		endorsementUpdateObj,
		productConfig,
		selectedExposure,
		setLoading,
		fields,
		saveFastForm,
	} = endorsementData;

	if (!selectedExposure || !endorsementUpdateObj) {
		return <LargeCenterSpinner />;
	}

	const { perilId, exposureId } = params;

	// Only added exposures have displayId
	const isExposureAdded = !(selectedExposure as Exposure).displayId;
	const exposureLocator =
		(selectedExposure as Exposure).locator ||
		(selectedExposure as ExposureCreateRequest).exposureLocator;
	const exposureIndex = getExposureIndexFromContext(
		endorsementUpdateObj,
		exposureLocator,
	);

	// selectedExposure differs from endorsementExposureObj because selectedExposure can be Exposure and
	// cannot be EndorsementExposureUpdateRequest
	const endorsementExposureObj = getExposureObject(
		endorsementUpdateObj,
		exposureLocator,
	);
	const selectedPerilIndex = parseInt(perilId.split('-')[1], 10);
	const addedExposureIndex = parseInt(exposureId.split('-')[1], 10);

	const isInEditMode =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	if (!Number.isInteger(selectedPerilIndex)) {
		return null;
	}

	const selectedPeril = isExposureAdded
		? (endorsementExposureObj as ExposureCreateRequest).perils?.[
				selectedPerilIndex
		  ]
		: (endorsementExposureObj as EndorsementExposureUpdateRequest).addPerils?.[
				selectedPerilIndex
		  ];

	if (!selectedPeril) {
		return null;
	}

	const exposureName =
		(selectedExposure as Exposure).name ||
		(selectedExposure as ExposureCreateRequest).exposureName;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(exposure) => exposure.name === exposureName,
	);
	const perilConfig = exposureConfig.perils.find(
		(p) => p.name === selectedPeril.name,
	);
	const perilConfigs = exposureConfig.perils;

	if (!perilConfig) {
		return null;
	}

	const displayName = getPerilName(
		selectedPeril,
		perilConfig.displayName,
		selectedExposure,
		null,
		endorsementUpdateObj,
	);
	const isEditable =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const removePeril = async () => {
		try {
			setLoading(true);
			setIsConfirmModalOpen(false);
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeAddedPeril: {
					perilLocator: selectedPeril.locator,
					exposureLocator,
					exposureIsAdded: isExposureAdded,
					exposureIndex,
				},
			});
			navigate(
				`/policy/${params.policyId}/endorsements/${params.endorsementId}/exposures/${params.exposureId}/perils`,
			);
			successToast('Successfully removed peril');
		} catch (e) {
			errorToast('Error removing peril');
		} finally {
			setLoading(false);
		}
	};

	const renderRemovePeril = () => {
		return (
			<Tooltip title={'Remove ' + t('peril.singular.upper')}>
				<IconButton
					aria-label='Remove'
					sx={{ position: 'relative', top: '8px' }}
					onClick={() => setIsConfirmModalOpen(true)}
					className={classes.removeIcon}
				>
					<DeleteIcon
						sx={{
							'&:hover': {
								backgroundColor: '#f4433',
							},
						}}
					/>
				</IconButton>
			</Tooltip>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	const getFormName = () =>
		exposureId.includes('added')
			? `exposure-${addedExposureIndex}.peril-${selectedPerilIndex}`
			: exposureLocator + '.' + `peril-${selectedPerilIndex}`;

	return (
		<>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={RouterLink}
						to='auxData'
					>
						Aux Data
					</MaterialLink>
				)}
				{isEditable && renderRemovePeril()}
				{isConfirmModalOpen && renderRemovePerilModal()}
			</Box>
			<hr />
			{!hidePerilLimitsEnabled ? <h4>Limits</h4> : null}
			<Grid container spacing={2}>
				<FastForm
					formName={getFormName()}
					form={fields}
					configVersion={productConfig.configVersion}
					authData={authData}
					productName={productConfig.name}
					disabled={!isInEditMode || userHasWriteAccess}
					save={saveFastForm}
					setLoading={setLoading}
					effectiveDate={endorsementUpdateObj.startTimestamp}
					disableExternalCalls
				/>
			</Grid>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(AddedPerilDetails);
